/**
 * Plantillas de email: mensajes habituales (pedir documentación, recordar un pago…) con
 * variables `{{cliente.displayName}}` que se rellenan al escribir a un cliente concreto.
 * Reutilizan la sintaxis de las plantillas de escritos, pero con su propio catálogo: solo
 * lo que tiene sentido en un correo.
 */
import { z } from 'zod';
import { singleLine } from './communications';
export const EMAIL_TEMPLATE_VARIABLES = [
    { key: 'cliente.displayName', description: 'Nombre o razón social del cliente' },
    { key: 'cliente.email', description: 'Email del cliente' },
    { key: 'cliente.phone', description: 'Teléfono del cliente' },
    { key: 'causa.code', description: 'Código del expediente (requiere elegir expediente)' },
    { key: 'causa.title', description: 'Título del expediente (requiere elegir expediente)' },
    { key: 'causa.courtName', description: 'Juzgado o tribunal (requiere elegir expediente)' },
    {
        key: 'causa.courtFileNumber',
        description: 'N.º de procedimiento (requiere elegir expediente)',
    },
    { key: 'causa.opposingParty', description: 'Parte contraria (requiere elegir expediente)' },
    { key: 'bufete.name', description: 'Nombre del bufete' },
    { key: 'bufete.phone', description: 'Teléfono del bufete' },
    { key: 'bufete.email', description: 'Email del bufete' },
    { key: 'usuario.fullName', description: 'Quien envía el mensaje' },
    { key: 'usuario.email', description: 'Email de quien envía el mensaje' },
    { key: 'fecha.hoy', description: 'Fecha de hoy (dd/mm/aaaa)' },
    { key: 'fecha.hoyLargo', description: 'Fecha de hoy («27 de mayo de 2026»)' },
];
/** Misma sintaxis que `applyVariables` de las plantillas de escritos. */
const PLACEHOLDER_SOURCE = String.raw `\{\{\s*([a-zA-Z_]\w*)\.([a-zA-Z_]\w*)\s*\}\}`;
/** Claves `sección.nombre` de todos los `{{…}}` que contiene el texto, sin repetir. */
export function findPlaceholders(text) {
    const found = new Set();
    for (const m of text.matchAll(new RegExp(PLACEHOLDER_SOURCE, 'g')))
        found.add(`${m[1]}.${m[2]}`);
    return [...found];
}
export const EmailTemplateInputSchema = z
    .object({
    name: z.string().trim().min(1).max(100),
    subject: singleLine(200),
    body: z.string().trim().min(1).max(10_000),
})
    .superRefine((v, ctx) => {
    const known = new Set(EMAIL_TEMPLATE_VARIABLES.map((x) => x.key));
    for (const key of findPlaceholders(`${v.subject}\n${v.body}`)) {
        if (!known.has(key)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['body'],
                message: `La variable {{${key}}} no existe`,
            });
        }
    }
});
export const EmailTemplateDtoSchema = z.object({
    id: z.string(),
    name: z.string(),
    subject: z.string(),
    body: z.string(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
    createdByName: z.string().nullable(),
});
export const EmailTemplateListResponseSchema = z.object({
    items: z.array(EmailTemplateDtoSchema),
    variables: z.array(z.object({ key: z.string(), description: z.string() })),
});
export const RenderEmailTemplateSchema = z.object({
    caseId: z.string().nullable().optional(),
});
export const RenderedEmailTemplateSchema = z.object({
    subject: z.string(),
    body: z.string(),
    /** `{{…}}` que no se han podido rellenar (p. ej. del expediente sin elegir uno). */
    unresolved: z.array(z.string()),
});
//# sourceMappingURL=email-templates.js.map